import { defineNuxtPlugin, useNuxtApp } from '#app';
import * as autopesuTokens from '../assets/scss/themes/st1-autopesu/_export.module.scss';
import * as vierumakiTokens from '../assets/scss/themes/vierumaki/_export.module.scss';
import { useCartStore } from '~dsc/stores/cms/cart';

const tokens = {
    'st1-autopesu': {
        'primary': autopesuTokens.default['color-primary-500'],
        'secondary': autopesuTokens.default['color-secondary-500'],
        'tertiary': autopesuTokens.default['color-tertiary-500'],
    },
    'vierumaki': {
        'primary': vierumakiTokens.default['color-primary-500'],
        'secondary': vierumakiTokens.default['color-secondary-500'],
        'tertiary': vierumakiTokens.default['color-tertiary-500'],
    },
};

export default defineNuxtPlugin(() => ({
    provide: {
        cms: {
            localeCodeToIso(code) {
                const { $i18n } = useNuxtApp();
                try {
                    return locales.find(locale => locale.code === code).iso;
                } catch (e) {
                    return locales.find(locale => locale.code === $i18n.defaultLocale).iso;
                }
            },
            localizeLink(path) {
                const { $i18n, $paths } = useNuxtApp();
                const resolvedPath = [];

                if ($i18n.locale.value !== $i18n.defaultLocale && $i18n.strategy !== 'no_prefix') {
                    resolvedPath.push(`${$i18n.locale.value}`);
                }

                if (path !== '/') {
                    resolvedPath.push(path);
                }

                if (resolvedPath.length > 0) {
                    return `/${resolvedPath.join('/')}`;
                }

                if ($paths) {
                    return $paths.getPath('landingPage');
                }

                return '/';
            },
            parseLink(link) {
                if (!link) {
                    return null;
                }

                const { url, openInNewTab = true, image } = link;
                const { $cms } = useNuxtApp();
                const fallbackUrl = { to: '/' };

                if (typeof (url) !== 'object') return fallbackUrl;

                const { contentType, slug, value, type, url: path } = url;

                if (['articlePage', 'standardPage'].includes(contentType)) {
                    return {
                        to: $cms.localizeLink(slug),
                        image: image ? image[0] : null,
                    };
                } else if (contentType === 'string') {
                    return {
                        title: link.title ?? 'Link',
                        href: value,
                        target: openInNewTab ? '_blank' : '_self',
                        image: image ? image[0] : null,
                    };
                } else if (contentType === 'link' && type === 'internal') {
                    return {
                        to: $cms.localizeLink(path),
                        image: image ? image[0] : null,
                    };
                } else if (contentType === 'card') {
                    return {
                        button: true,
                        dialog: link.url,
                        image: image ? image[0] : null,
                    };
                }

                return fallbackUrl;
            },
            roundToTwo(num) {
                return (Math.round((Number(num) + Number.EPSILON) * 100) / 100).toFixed(2);
            },
            formatPrice(price) {
                return `${this.roundToTwo(price).replace('.', ',')} €`;
            },
            evaluateForeground(inputColor) {
                let color = inputColor?.substring(1);

                if (color?.length < 6) {
                    color = color[0] + color[0] + color[1] + color[1] + color[2] + color[2];

                    const rgb = parseInt(color, 16);
                    const r = (rgb >> 16) & 0xff;
                    const g = (rgb >> 8) & 0xff;
                    const b = (rgb >> 0) & 0xff;

                    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;

                    if (luma < 110) {
                        return 'fg-light';
                    }
                }

                return 'fg-dark';
            },
            computedBackgroundClass(display, theme = 'st1-autopesu') {
                const { $cms } = useNuxtApp();

                if (['primary', 'secondary', 'tertiary'].includes(display.background)) {
                    return `bg-${display.background} ${$cms.evaluateForeground(tokens[theme][display.background])}`;
                } else if (display.background === 'fullbg') {
                    return 'full-bg';
                } else if (display.background === 'none') {
                    return [];
                }

                return `bg-custom ${$cms.evaluateForeground(display.background)}`;
            },
            findCurrentDay(openingHours, date) {
                const { $cms } = useNuxtApp();
                const today = date ? new Date(date) : new Date();

                return openingHours.find(openingHoursEntry => {
                    const { date = '' } = openingHoursEntry;

                    return $cms.isSameDate(new Date(date), today);
                });
            },
            isSameDate(openingHourDate, today) {
                const dateDifferenceInMillis = Math.abs(openingHourDate - today);
                const oneDayInMillis = 1000 * 3600 * 24;
                const isSameDay = openingHourDate.getDay() === today.getDay();

                return dateDifferenceInMillis < oneDayInMillis && isSameDay;
            },
            productArticle(products) {
                return products?.find(item => item.isBundle === false) || {};
            },
            addProduct(entry, silent = false) {
                const { $i18n } = useNuxtApp();
                const isBundle = entry.bundleMembers?.length > 0;

                const amount = isBundle ? '' : `1 ${$i18n.t('pcs')}`;
                const price = entry.attributes?.price ? entry.attributes.price : entry.unitPrice;
                const name = entry.attributes?.product_name ? `${entry.attributes.product_name} ${amount}` : entry.name;
                const includes = isBundle ? entry.bundleMembers.map(item => ({
                    amount: item.amount,
                    globalName: item.product?.globalName ?? '',
                    sku: !isBundle ? [product.code] : entry.bundleMembers.map(item => item.product.code),
                })) : [];

                const productItem = {
                    code: entry.code,
                    name,
                    image: entry.image ?? entry.images,
                    amount: 1,
                    price,
                    includes,
                    attributes: {
                        price,
                        tax_class: entry.attributes?.tax_class,
                    },
                    showUpsell: entry.showUpsell || entry.showUpsell === undefined,
                };

                useCartStore().addToCart(productItem, silent);
            },
        },
    },
}));
